import { findBestAudioTrackForLanguage, summarizeAudioTracks } from '../tracks/audioTracks';
import type { AudioTrackSummary } from '../tracks/types';
import { log } from '../logging/logger';

function currentAudioSummaries(
  playerManager: framework.PlayerManager,
): AudioTrackSummary[] {
  const tracks = playerManager.getMediaInformation()?.tracks ?? [];
  return summarizeAudioTracks(
    tracks.map((t: framework.messages.Track) => ({
      trackId: t.trackId,
      type: String(t.type),
      language: t.language,
      name: t.name,
    })),
  );
}

function languageNotSupported(language: string): framework.messages.ErrorData {
  const err = new cast.framework.messages.ErrorData(
    cast.framework.messages.ErrorType.INVALID_REQUEST,
  );
  err.reason = cast.framework.messages.ErrorReason.LANGUAGE_NOT_SUPPORTED;
  log.warn('TRACKS', 'language not supported', { language });
  return err;
}

export function installEditAudioTracksInterceptor(playerManager: framework.PlayerManager): void {
  playerManager.setMessageInterceptor(
    cast.framework.messages.MessageType.EDIT_AUDIO_TRACKS,
    (request: framework.messages.EditAudioTracksRequestData) => {
      const language = request.language ?? '';
      const summaries = currentAudioSummaries(playerManager);

      if (summaries.length === 0) {
        log.warn('TRACKS', 'EDIT_AUDIO_TRACKS before tracks parsed', { requested: language });
        return request;
      }

      const match = findBestAudioTrackForLanguage(summaries, language);
      if (!match) return languageNotSupported(language);

      log.info('TRACKS', 'EDIT_AUDIO_TRACKS', {
        requested: language || '(empty)',
        matchedLanguage: match.normalizedLang || '(none)',
        matchedId: match.id,
      });

      return request;
    },
  );

  playerManager.setMessageInterceptor(
    cast.framework.messages.MessageType.EDIT_TRACKS_INFO,
    (request: framework.messages.EditTracksInfoRequestData) => {
      const summaries = currentAudioSummaries(playerManager);
      if (summaries.length === 0) return request;

      if (request.language && request.language.length > 0) {
        const match = findBestAudioTrackForLanguage(summaries, request.language);
        if (!match) return languageNotSupported(request.language);

        const existingIds = (request.activeTrackIds ?? []).filter(
          (id: number) => !summaries.some((s) => s.id === id),
        );
        request.activeTrackIds = [...existingIds, match.id];

        log.info('TRACKS', 'EDIT_TRACKS_INFO (language)', {
          requested: request.language,
          matchedId: match.id,
          activeTrackIds: (request.activeTrackIds ?? []).join(','),
        });
        return request;
      }

      log.info('TRACKS', 'EDIT_TRACKS_INFO (activeTrackIds)', {
        activeTrackIds: (request.activeTrackIds ?? []).join(','),
      });
      return request;
    },
  );
}
